import React, { useState, useCallback, useEffect, useRef } from 'react';
import { STORAGE_KEY, IMPREJURARI_DATA, getInitialFormData, loadFromStorage } from '../constants';
import { AccidentSketch, ManualSketchGrid, RadioGroup } from '../components';
import { SCENARIO_LIST } from '../sketchScenarios';

// Exemple pentru descriere accident
const EXEMPLE_DESCRIERI = [
  "La intersectie am virat la stanga. Celalalt sofer venea din sens opus si m-a lovit in dreapta.",
  "Eram oprit la semafor. Vehiculul din spate nu a franat la timp si m-a lovit in spate.",
  "Ieseam din parcare cu spatele. Alt vehicul trecea pe culoar si ne-am lovit lateral.",
  "Circulam intr-un sens giratoriu. Celalalt vehicul a intrat fara sa acorde prioritate."
];

const MAX_PDF_GENERATIONS = 3;
const PDF_COUNT_KEY = 'amiabila_pdf_count';

// Definitia pasilor wizard - 5 PASI cu texte ghid pentru sidebar
const WIZARD_STEPS = [
  { 
    id: 1, title: 'Date accident', icon: '📍',
    guideTitle: 'Când și unde?',
    guideText: 'Completează data, ora și locul accidentului. Dacă sunt martori, adaugă-le datele de contact. Durează sub un minut.',
    guideTip: 'Locul accidentului trebuie să fie cât mai exact: strada, numărul, orașul.'
  },
  { 
    id: 2, title: 'Vehicule', icon: '🚗',
    guideTitle: 'Datele vehiculelor',
    guideText: 'Introdu datele ambelor mașini implicate: proprietar, mașină, asigurare RCA și conducător auto.',
    guideTip: 'Nu contează cine completează secțiunea A sau B. Important e ca ambii șoferi să semneze la final.'
  },
  { 
    id: 3, title: 'Împrejurări', icon: '📋',
    guideTitle: 'Ce s-a întâmplat?',
    guideText: 'Bifează situațiile care descriu accidentul pentru fiecare vehicul. Este important ca ambii conducători să fie de acord.',
    guideTip: 'Poți bifa mai multe situații. Alege-le pe cele care descriu cel mai bine ce a făcut fiecare vehicul.'
  },
  { 
    id: 4, title: 'Schița', icon: '🎨',
    guideTitle: 'Desenează accidentul',
    guideText: 'Alege un scenariu predefinit care se potrivește sau generează automat o schiță cu AI. Dacă niciuna nu corespunde, lasă grila pentru desen manual.',
    guideTip: 'Pentru generarea AI, descrie cât mai detaliat: tipul drumului, direcțiile vehiculelor, punctul de impact.'
  },
  { 
    id: 5, title: 'Finalizare', icon: '✅',
    guideTitle: 'Aproape gata!',
    guideText: 'Adaugă observații dacă e cazul, verifică toate datele introduse și descarcă PDF-ul completat. Nu uita: trebuie semnat de ambii conducători!',
    guideTip: 'PDF-ul descărcat este formularul oficial de Constatare Amiabilă, acceptat de toate asigurările din România.'
  }
];

// ========== SUCCESS CARD COMPONENT ==========
function SuccessCard({ emailSent, emailAddress }) {
  return (
    <div className="success-card">
      <div className="success-card-header">
        <span className="success-card-icon">✅</span>
        <div>
          <strong>PDF generat cu succes!</strong>
          {emailSent && <span className="success-email-note"> • Email trimis la {emailAddress}</span>}
        </div>
      </div>
      
      <div className="success-card-info">
        <p><strong>Nu uita:</strong> Amiabila trebuie semnata de ambii conducatori implicati in accident!</p>
      </div>
      
      <div className="success-card-cta">
        <div className="success-cta-text">
          <strong>🚗 Vino la Autoklasen</strong> — Te asteptam sa deschizi dosarul de dauna!
        </div>
        <div className="success-cta-contact">
          <a href="tel:0371719191" className="success-phone-link">📞 0371 71 91 91</a>
          <span className="success-address">📍 Bd. Pallady 287, Sector 3</span>
        </div>
      </div>
    </div>
  );
}

// ========== COMPACT HERO ==========
function HeroMini() {
  return (
    <section className="hero-mini">
      <div className="hero-mini-content">
        <div className="hero-mini-logo">
          <img 
            src="/logo-autoklasen.png" 
            alt="Autoklasen" 
            className="hero-logo-img"
            onError={(e) => { 
              e.target.style.display = 'none'; 
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <span className="hero-logo-fallback" style={{display: 'none'}}>🚗</span>
        </div>
        <h1 className="hero-mini-title">
          Constatare Amiabilă
          <span className="hero-title-accent"> online</span>
        </h1>
        <p className="hero-mini-subtitle">
          Gratuit · 5 minute · PDF gata de semnat · Acceptat de toate asigurările
        </p>
        <button 
          className="hero-cta-btn"
          onClick={() => {
            const formEl = document.querySelector('.form-main, .form-page-layout');
            if (formEl) formEl.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="12" y1="18" x2="12" y2="12"/>
            <line x1="9" y1="15" x2="15" y2="15"/>
          </svg>
          Completează formularul
        </button>
        <p className="hero-mini-note">Pentru Windows, MacOS și mobil</p>
      </div>
    </section>
  );
}

// ========== SIDEBAR GUIDE (doar desktop) ==========
function SidebarGuide({ currentStep }) {
  const step = WIZARD_STEPS.find(s => s.id === currentStep);
  
  return (
    <aside className="form-sidebar">
      <div className="sidebar-inner">
        {/* Preview imagine formular */}
        <div className="sidebar-preview">
          <img 
            src="/constatare-amiabila-preview.jpg" 
            alt="Preview Constatare Amiabilă" 
            className="sidebar-preview-img"
          />
        </div>

        {/* Ghid contextual per pas */}
        <div className="sidebar-guide">
          <div className="sidebar-step-badge">
            <span className="sidebar-step-icon">{step.icon}</span>
            Pasul {currentStep} din 5
          </div>
          <h3 className="sidebar-guide-title">{step.guideTitle}</h3>
          <p className="sidebar-guide-text">{step.guideText}</p>
          <div className="sidebar-tip">
            <span className="sidebar-tip-icon">💡</span>
            <p>{step.guideTip}</p>
          </div>
        </div>

        {/* Trust signals */}
        <div className="sidebar-trust">
          <div className="sidebar-trust-item">
            <span>✓</span> Format standard european
          </div>
          <div className="sidebar-trust-item">
            <span>✓</span> Toate câmpurile pre-completate
          </div>
          <div className="sidebar-trust-item">
            <span>✓</span> Schița accidentului inclusă
          </div>
          <div className="sidebar-trust-item">
            <span>✓</span> Fără cont, fără costuri ascunse
          </div>
        </div>

        {/* Contact ajutor */}
        <div className="sidebar-help">
          Ai nevoie de ajutor? Sună-ne la<br />
          <a href="tel:0371719191"><strong>0371 71 91 91</strong></a>
        </div>
      </div>
    </aside>
  );
}

// ========== MAIN PAGE ==========
function FormularPage() {
  return (
    <>
      <HeroMini />
      <FormContent />
    </>
  );
}

// ========== WIZARD STEPPER (progress bar minimala via CSS) ==========
function WizardStepper({ currentStep, onStepClick, completedSteps }) {
  return (
    <>
      <div className="wizard-stepper">
        {WIZARD_STEPS.map((step) => {
          const isActive = step.id === currentStep;
          const isCompleted = completedSteps.includes(step.id);
          const isClickable = step.id <= Math.max(...completedSteps, currentStep);
          
          return (
            <div 
              key={step.id}
              className={`wizard-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isClickable ? 'clickable' : ''}`}
              onClick={() => isClickable && onStepClick(step.id)}
              title={step.title}
            >
              <div className="wizard-step-icon">
                {isCompleted && !isActive ? '✓' : step.icon}
              </div>
              <div className="wizard-step-title">{step.title}</div>
            </div>
          );
        })}
      </div>
      <div className="progress-label">
        <strong>Pasul {currentStep}/5</strong> — {WIZARD_STEPS.find(s => s.id === currentStep)?.title}
      </div>
    </>
  );
}

// ========== FORM CONTENT ==========
function FormContent() {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', error: false });
  const [sketchMode, setSketchMode] = useState('select');
  const [descriereAccident, setDescriereAccident] = useState('');
  const [sketchData, setSketchData] = useState(null);
  const [sketchLoading, setSketchLoading] = useState(false);
  const [sketchError, setSketchError] = useState('');
  const [formData, setFormData] = useState(loadFromStorage);
  const [emailAddress, setEmailAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [pdfGenerated, setPdfGenerated] = useState(false);
  const [emailWasSent, setEmailWasSent] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [pdfGenerationCount, setPdfGenerationCount] = useState(() => {
    try { return parseInt(localStorage.getItem(PDF_COUNT_KEY)) || 0; } catch { return 0; }
  });
  
  const formContainerRef = useRef(null);
  
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    } catch (e) {
      console.error('Eroare la salvarea datelor:', e);
    }
  }, [formData]);

  const showToast = useCallback((message, error = false) => {
    setToast({ show: true, message, error });
    setTimeout(() => setToast({ show: false, message: '', error: false }), 3000);
  }, []);

  const updateFormData = useCallback((path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  }, []);

  const toggleImprejurare = useCallback((vehicle, id) => {
    const key = vehicle === 'A' ? 'imprejurariA' : 'imprejurariB';
    setFormData(prev => ({
      ...prev,
      [key]: prev[key].includes(id) ? prev[key].filter(i => i !== id) : [...prev[key], id]
    }));
  }, []);

  const handleSketchModeChange = useCallback((mode) => {
    setSketchMode(mode);
    if (mode === 'manual') {
      setSketchData(null);
      setFormData(prev => ({ ...prev, sketchData: null }));
      setDescriereAccident('');
      setSketchError('');
    }
  }, []);

  const copyOwnerToDriver = useCallback((vehicle) => {
    const prefix = `vehicul${vehicle}`;
    setFormData(prev => {
      const vehicleData = prev[prefix];
      const asigurat = vehicleData.asigurat;
      return {
        ...prev,
        [prefix]: {
          ...vehicleData,
          conducatorEsteProprietar: true,
          conducator: {
            ...vehicleData.conducator,
            nume: asigurat.nume,
            prenume: asigurat.prenume,
            adresa: asigurat.adresa,
            tara: asigurat.tara,
            tel: asigurat.tel
          }
        }
      };
    });
    showToast(`Date copiate pentru Vehicul ${vehicle}`);
  }, [showToast]);

  const uncheckOwnerToDriver = useCallback((vehicle) => {
    const prefix = `vehicul${vehicle}`;
    setFormData(prev => ({
      ...prev,
      [prefix]: { ...prev[prefix], conducatorEsteProprietar: false }
    }));
  }, []);

  const resetForm = useCallback(() => {
    if (window.confirm('Sigur doriti sa stergeti toate datele din formular?')) {
      setFormData(getInitialFormData());
      setSketchData(null);
      setDescriereAccident('');
      setSketchError('');
      setSketchMode('select');
      setCurrentStep(1);
      setCompletedSteps([]);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(PDF_COUNT_KEY);
      setPdfGenerationCount(0);
      showToast('Formularul a fost resetat');
    }
  }, [showToast]);

  const scrollToFormTop = useCallback(() => {
    setTimeout(() => {
      formContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, []);

  const goToStep = useCallback((step) => {
    setCurrentStep(step);
    scrollToFormTop();
  }, [scrollToFormTop]);

  const nextStep = useCallback(() => {
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps(prev => [...prev, currentStep]);
    }
    if (currentStep < WIZARD_STEPS.length) {
      goToStep(currentStep + 1);
    }
  }, [currentStep, completedSteps, goToStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    }
  }, [currentStep, goToStep]);

  const genereazaSchita = useCallback(async () => {
    if (!descriereAccident.trim()) {
      setSketchError('Va rugam descrieti accidentul.');
      return;
    }
    setSketchLoading(true);
    setSketchError('');
    try {
      const response = await fetch('/api/generate-sketch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ descriere: descriereAccident })
      });
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Serverul nu a returnat JSON. Reporneste serverul.');
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.details || data.error || 'Eroare');
      setSketchData(data.sketchData);
      setFormData(prev => ({ ...prev, sketchData: data.sketchData }));
      showToast('Schita a fost generata!');
    } catch (e) {
      setSketchError(e.message);
    } finally {
      setSketchLoading(false);
    }
  }, [descriereAccident, showToast]);

  const selectScenario = useCallback((scenarioId) => {
    if (!scenarioId) {
      setSketchData(null);
      setFormData(prev => ({ ...prev, sketchData: null }));
      return;
    }
    const newSketchData = { scenarioId };
    setSketchData(newSketchData);
    setFormData(prev => ({ ...prev, sketchData: newSketchData }));
    showToast('Scenariu selectat');
  }, [showToast]);

  const generatePDF = useCallback(async () => {
    const required = [
      'dataAccident', 
      'locAccident', 
      'vehiculA.asigurat.nume', 
      'vehiculA.vehicul.marca', 
      'vehiculA.vehicul.nrInmatriculare', 
      'vehiculA.asigurare.denumire', 
      'vehiculA.asigurare.politaNr',
      'vehiculA.conducator.nume', 
      'vehiculB.asigurat.nume', 
      'vehiculB.vehicul.marca',
      'vehiculB.vehicul.nrInmatriculare', 
      'vehiculB.asigurare.denumire', 
      'vehiculB.asigurare.politaNr',
      'vehiculB.conducator.nume'
    ];
    
    for (const path of required) {
      const value = path.split('.').reduce((obj, key) => obj?.[key], formData);
      if (!value?.trim()) {
        showToast(`Completati: ${path.split('.').pop()}`, true);
        return;
      }
    }

    if (!emailAddress.trim()) {
      showToast('Introduceti adresa de email', true);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress)) {
      showToast('Adresa de email nu este valida', true);
      return;
    }
    if (!phoneNumber.trim()) {
      showToast('Introduceti numarul de telefon', true);
      return;
    }

    setLoading(true);
    try {
      const { generateAmiabilaPDF } = await import('../pdfGenerator');
      const pdfBytes = await generateAmiabilaPDF(formData);
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `Amiabila_${formData.dataAccident || 'document'}.pdf`;
      link.click();
      showToast('PDF descarcat cu succes!');
      
      // Incrementează contorul de generări
      const newCount = pdfGenerationCount + 1;
      setPdfGenerationCount(newCount);
      try { localStorage.setItem(PDF_COUNT_KEY, newCount.toString()); } catch {}
      
      let emailSentSuccess = false;
      setEmailLoading(true);
      try {
        const reader = new FileReader();
        const base64Promise = new Promise((resolve) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(blob);
        });
        const pdfBase64 = await base64Promise;
        
        const emailResponse = await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: emailAddress,
            phone: phoneNumber,
            pdfBase64,
            fileName: `Amiabila_${formData.dataAccident || 'document'}.pdf`,
            formData: {
              dataAccident: formData.dataAccident,
              locAccident: formData.locAccident,
              vehiculA: formData.vehiculA?.vehicul?.marca,
              vehiculB: formData.vehiculB?.vehicul?.marca
            }
          })
        });
        
        if (emailResponse.ok) {
          emailSentSuccess = true;
          showToast('Email trimis cu succes!');
        }
      } catch (emailErr) {
        console.error('Eroare email:', emailErr);
      } finally {
        setEmailLoading(false);
      }
      
      setEmailWasSent(emailSentSuccess);
      setPdfGenerated(true);
      
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setLoading(false);
    }
  }, [formData, showToast, emailAddress, phoneNumber]);

  // Render formular vehicul
  const renderVehicleForm = (vehicle) => {
    const prefix = `vehicul${vehicle}`;
    const data = formData[prefix];
    const isA = vehicle === 'A';
    
    return (
      <div className={`form-section vehicle-${vehicle.toLowerCase()}`}>
        <div className="section-header">
          <div className={`section-icon ${isA ? 'blue' : 'orange'}`}>{isA ? '🚗' : '🚙'}</div>
          <h2>Vehicul {vehicle}</h2>
          <span className={`vehicle-badge badge-${vehicle.toLowerCase()}`}>{vehicle}</span>
        </div>

        <div className="subsection-title">6. Asigurat / Proprietar</div>
        <div className="form-row">
          <div className="form-group no-margin">
            <label className="form-label">Nume <span className="required">*</span></label>
            <input type="text" className="form-input" value={data.asigurat.nume}
              onChange={e => updateFormData(`${prefix}.asigurat.nume`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Prenume</label>
            <input type="text" className="form-input" value={data.asigurat.prenume}
              onChange={e => updateFormData(`${prefix}.asigurat.prenume`, e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Adresa</label>
          <input type="text" className="form-input" value={data.asigurat.adresa}
            onChange={e => updateFormData(`${prefix}.asigurat.adresa`, e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group no-margin">
            <label className="form-label">Cod postal</label>
            <input type="text" className="form-input" value={data.asigurat.codPostal}
              onChange={e => updateFormData(`${prefix}.asigurat.codPostal`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Tara</label>
            <input type="text" className="form-input" value={data.asigurat.tara}
              onChange={e => updateFormData(`${prefix}.asigurat.tara`, e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Telefon / Email</label>
          <input type="text" className="form-input" value={data.asigurat.tel}
            onChange={e => updateFormData(`${prefix}.asigurat.tel`, e.target.value)} />
        </div>

        <div className="subsection-title">7. Vehicul cu motor</div>
        <div className="form-group">
          <label className="form-label">Marca, tip <span className="required">*</span></label>
          <input type="text" className="form-input" placeholder="ex: Volkswagen Golf 7" value={data.vehicul.marca}
            onChange={e => updateFormData(`${prefix}.vehicul.marca`, e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group no-margin">
            <label className="form-label">Nr. inmatriculare <span className="required">*</span></label>
            <input type="text" className="form-input" placeholder="ex: B 123 ABC" value={data.vehicul.nrInmatriculare}
              onChange={e => updateFormData(`${prefix}.vehicul.nrInmatriculare`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Tara inmatricularii</label>
            <input type="text" className="form-input" value={data.vehicul.taraInmatriculare}
              onChange={e => updateFormData(`${prefix}.vehicul.taraInmatriculare`, e.target.value)} />
          </div>
        </div>

        <div className="subsection-title">7. Remorca (optional)</div>
        <div className="form-row">
          <div className="form-group no-margin">
            <label className="form-label">Nr. inmatriculare remorca</label>
            <input type="text" className="form-input" placeholder="ex: B 456 XYZ" value={data.remorca?.nrInmatriculare || ''}
              onChange={e => updateFormData(`${prefix}.remorca.nrInmatriculare`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Tara inmatricularii</label>
            <input type="text" className="form-input" value={data.remorca?.taraInmatriculare || 'Romania'}
              onChange={e => updateFormData(`${prefix}.remorca.taraInmatriculare`, e.target.value)} />
          </div>
        </div>

        <div className="subsection-title">8. Societate de asigurare</div>
        <div className="form-group">
          <label className="form-label">Denumirea asiguratorului <span className="required">*</span></label>
          <input type="text" className="form-input" placeholder="ex: Euroins, Groupama, Allianz" value={data.asigurare.denumire}
            onChange={e => updateFormData(`${prefix}.asigurare.denumire`, e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group no-margin">
            <label className="form-label">Nr. polita <span className="required">*</span></label>
            <input type="text" className="form-input" placeholder="Nr. polita RCA" value={data.asigurare.politaNr}
              onChange={e => updateFormData(`${prefix}.asigurare.politaNr`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Valabila de la</label>
            <input type="date" className="form-input" value={data.asigurare.valabilDeLa}
              onChange={e => updateFormData(`${prefix}.asigurare.valabilDeLa`, e.target.value)} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group no-margin">
            <label className="form-label">Valabila pana la</label>
            <input type="date" className="form-input" value={data.asigurare.valabilPanaLa}
              onChange={e => updateFormData(`${prefix}.asigurare.valabilPanaLa`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Nr. carte verde</label>
            <input type="text" className="form-input" value={data.asigurare.carteVerde}
              onChange={e => updateFormData(`${prefix}.asigurare.carteVerde`, e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Asigurarea acopera daune tari terte?</label>
          <RadioGroup value={data.asigurare.acoperaDaune} onChange={v => updateFormData(`${prefix}.asigurare.acoperaDaune`, v)} />
        </div>

        <div className="subsection-title">9. Conducator auto</div>
        <div className="checkbox-row">
          <input type="checkbox" id={`same-${vehicle}`}
            checked={data.conducatorEsteProprietar}
            onChange={e => e.target.checked ? copyOwnerToDriver(vehicle) : uncheckOwnerToDriver(vehicle)} />
          <label htmlFor={`same-${vehicle}`}>Acelasi cu proprietarul</label>
        </div>
        <div className="form-row">
          <div className="form-group no-margin">
            <label className="form-label">Nume <span className="required">*</span></label>
            <input type="text" className="form-input" value={data.conducator.nume}
              onChange={e => updateFormData(`${prefix}.conducator.nume`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Prenume</label>
            <input type="text" className="form-input" value={data.conducator.prenume}
              onChange={e => updateFormData(`${prefix}.conducator.prenume`, e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Adresa</label>
          <input type="text" className="form-input" value={data.conducator.adresa}
            onChange={e => updateFormData(`${prefix}.conducator.adresa`, e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Tara</label>
          <input type="text" className="form-input" value={data.conducator.tara}
            onChange={e => updateFormData(`${prefix}.conducator.tara`, e.target.value)} />
        </div>
        <div className="form-row-4">
          <div className="form-group no-margin">
            <label className="form-label">Nr. permis</label>
            <input type="text" className="form-input" value={data.conducator.nrPermis}
              onChange={e => updateFormData(`${prefix}.conducator.nrPermis`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Categoria</label>
            <input type="text" className="form-input" placeholder="B" value={data.conducator.categoriaPermis}
              onChange={e => updateFormData(`${prefix}.conducator.categoriaPermis`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Valabil de la</label>
            <input type="date" className="form-input" value={data.conducator.permisValabilDeLa}
              onChange={e => updateFormData(`${prefix}.conducator.permisValabilDeLa`, e.target.value)} />
          </div>
          <div className="form-group no-margin">
            <label className="form-label">Valabil pana la</label>
            <input type="date" className="form-input" value={data.conducator.permisValabilPanaLa}
              onChange={e => updateFormData(`${prefix}.conducator.permisValabilPanaLa`, e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Telefon</label>
          <input type="text" className="form-input" value={data.conducator.tel}
            onChange={e => updateFormData(`${prefix}.conducator.tel`, e.target.value)} />
        </div>

        <div className="subsection-title">10. Punct de impact initial</div>
        <div className="form-group">
          <label className="form-label">Indicati cu sageata punctul de impact initial</label>
          <select className="form-select" value={data.punctImpact}
            onChange={e => updateFormData(`${prefix}.punctImpact`, e.target.value)}>
            <option value="">-- Selectati --</option>
            <option value="fata">Fata</option>
            <option value="spate">Spate</option>
            <option value="stanga-fata">Stanga fata</option>
            <option value="stanga-spate">Stanga spate</option>
            <option value="dreapta-fata">Dreapta fata</option>
            <option value="dreapta-spate">Dreapta spate</option>
            <option value="lateral-stanga">Lateral stanga</option>
            <option value="lateral-dreapta">Lateral dreapta</option>
          </select>
        </div>

        <div className="subsection-title">11. Pagube materiale vizibile</div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <textarea className="form-textarea" placeholder="ex: Bara fata sparta, far stang spart" rows="2"
            value={data.pagube} onChange={e => updateFormData(`${prefix}.pagube`, e.target.value)} />
        </div>
      </div>
    );
  };

  // Render step content - 5 PASI
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="form-section full-width">
            <div className="section-header">
              <div className="section-icon blue">📍</div>
              <h2>Date despre accident</h2>
            </div>
            <div className="form-row">
              <div className="form-group no-margin">
                <label className="form-label">1. Data accidentului <span className="required">*</span></label>
                <input type="date" className="form-input" value={formData.dataAccident}
                  onChange={e => updateFormData('dataAccident', e.target.value)} />
              </div>
              <div className="form-group no-margin">
                <label className="form-label">Ora</label>
                <input type="time" className="form-input" value={formData.oraAccident}
                  onChange={e => updateFormData('oraAccident', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group no-margin">
                <label className="form-label">2. Locul accidentului <span className="required">*</span></label>
                <input type="text" className="form-input" placeholder="ex: Str. Mihai Eminescu nr. 10, Bucuresti"
                  value={formData.locAccident} onChange={e => updateFormData('locAccident', e.target.value)} />
              </div>
              <div className="form-group no-margin">
                <label className="form-label">Tara</label>
                <input type="text" className="form-input" value={formData.taraAccident}
                  onChange={e => updateFormData('taraAccident', e.target.value)} />
              </div>
            </div>
            <div className="form-row-3">
              <div className="form-group no-margin">
                <label className="form-label">3. Vatamari corporale?</label>
                <RadioGroup value={formData.vatamariCorporale} onChange={v => updateFormData('vatamariCorporale', v)} />
              </div>
              <div className="form-group no-margin">
                <label className="form-label">4. Pagube alte vehicule?</label>
                <RadioGroup value={formData.pagubeVehicule} onChange={v => updateFormData('pagubeVehicule', v)} />
              </div>
              <div className="form-group no-margin">
                <label className="form-label">Pagube alte obiecte?</label>
                <RadioGroup value={formData.pagubeObiecte} onChange={v => updateFormData('pagubeObiecte', v)} />
              </div>
            </div>
            <div className="form-group" style={{ marginTop: 8, marginBottom: 0 }}>
              <label className="form-label">5. Martori</label>
              <textarea className="form-textarea" placeholder="ex: Ion Popescu, Str. Victoriei 5, 0722.123.456"
                rows="2" value={formData.martori} onChange={e => updateFormData('martori', e.target.value)} />
            </div>
          </div>
        );

      case 2:
        return (
          <>
            <div className="form-section full-width" style={{ background: 'var(--ak-primary-light, #f0fdf4)', borderLeft: '3px solid var(--ak-primary, #16a34a)', padding: '14px', marginBottom: '16px' }}>
              <p style={{ fontSize: 13, color: '#166534', margin: 0, lineHeight: 1.5 }}>
                ℹ️ <strong>Nu contează cine completează secțiunea A sau B!</strong> Important este ca ambii șoferi să semneze la final.
              </p>
            </div>
            <div className="form-grid">
              {renderVehicleForm('A')}
              {renderVehicleForm('B')}
            </div>
          </>
        );

      case 3:
        return (
          <div className="form-section full-width">
            <div className="section-header">
              <div className="section-icon green">📋</div>
              <h2>12. Împrejurări</h2>
            </div>
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 14 }}>Bifați situațiile pentru fiecare vehicul:</p>
            <div className="imprejurari-grid">
              <div className="imprejurari-column col-a">
                <div className="imprejurari-header header-a">VEHICUL A ({formData.imprejurariA.length})</div>
                <div className="imprejurari-list">
                  {IMPREJURARI_DATA.map(item => (
                    <div key={item.id} className={`imprejurare-item ${formData.imprejurariA.includes(item.id) ? 'selected-a' : ''}`}
                      onClick={() => toggleImprejurare('A', item.id)}>
                      <div className="checkbox-box">{formData.imprejurariA.includes(item.id) && '✓'}</div>
                      <span><strong>{item.id}.</strong> {item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="imprejurari-column col-b">
                <div className="imprejurari-header header-b">VEHICUL B ({formData.imprejurariB.length})</div>
                <div className="imprejurari-list">
                  {IMPREJURARI_DATA.map(item => (
                    <div key={item.id} className={`imprejurare-item ${formData.imprejurariB.includes(item.id) ? 'selected-b' : ''}`}
                      onClick={() => toggleImprejurare('B', item.id)}>
                      <div className="checkbox-box">{formData.imprejurariB.includes(item.id) && '✓'}</div>
                      <span><strong>{item.id}.</strong> {item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="form-section full-width sketch-section">
            <div className="section-header">
              <div className="section-icon blue">🎨</div>
              <h2>13. Schița accidentului</h2>
            </div>
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 14, lineHeight: 1.6 }}>
              Alege un <strong>scenariu predefinit</strong>, <strong>generează cu AI</strong> sau lasă <strong>desen manual</strong> pe hârtie.
            </p>
            <div className="sketch-mode-toggle">
              <button type="button" className={`mode-btn ${sketchMode === 'select' ? 'active' : ''}`}
                onClick={() => handleSketchModeChange('select')}>Scenariu Predefinit</button>
              <button type="button" className={`mode-btn ${sketchMode === 'ai' ? 'active' : ''}`}
                onClick={() => handleSketchModeChange('ai')}>Generare AI</button>
              <button type="button" className={`mode-btn ${sketchMode === 'manual' ? 'active' : ''}`}
                onClick={() => handleSketchModeChange('manual')}>Desen Manual</button>
            </div>

            {sketchMode === 'manual' && (
              <div className="sketch-area">
                <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 0, textAlign: 'center' }}>
                  În PDF va apărea o grilă pentru desen manual.
                </p>
              </div>
            )}

            {sketchMode === 'ai' && (
              <div className="sketch-ai-area">
                <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>
                  Descrieți accidentul cât mai detaliat. <strong>Vehicul A = vehiculul dumneavoastră.</strong>
                </p>
                <div className="form-group">
                  <label className="form-label">Descrierea accidentului</label>
                  <textarea className="form-textarea" placeholder="Descrieți exact ce s-a întâmplat: tipul drumului, direcțiile, punctul de impact..." rows="4"
                    value={descriereAccident} onChange={e => setDescriereAccident(e.target.value)} />
                </div>
                <button type="button" className="btn-generate-sketch" onClick={genereazaSchita}
                  disabled={sketchLoading || !descriereAccident.trim()}>
                  {sketchLoading ? <><span className="spinner"></span> Se generează...</> : 'Generează Schița'}
                </button>
                {sketchError && <p style={{ color: '#DC2626', marginTop: 10 }}>{sketchError}</p>}
              </div>
            )}

            {sketchMode === 'select' && (
              <div className="sketch-ai-area">
                <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>
                  Alegeți un scenariu care se potrivește cu accidentul:
                </p>
                <div className="form-group">
                  <label className="form-label">Scenariu:</label>
                  <select className="form-select" 
                    onChange={e => selectScenario(e.target.value)}
                    value={sketchData?.scenarioId || ''}>
                    <option value="">-- Selectați un scenariu --</option>
                    {SCENARIO_LIST.map(s => (
                      <option key={s.id} value={s.id}>{s.name} - {s.description}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="sketch-display" style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'center', maxWidth: '100%' }}>
                {sketchData ? (
                  <AccidentSketch data={sketchData} width={360} height={260} />
                ) : (
                  <ManualSketchGrid width={360} height={260} />
                )}
              </div>
              <p style={{ fontSize: 12, color: '#6B7280', marginTop: 10, textAlign: 'center' }}>
                {sketchData ? 'Schița generată' : 'Schița se completează manual sau cu AI.'}
              </p>
              {sketchData && (
                <div style={{ marginTop: 10, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {sketchMode === 'ai' && (
                    <button type="button" onClick={genereazaSchita} className="btn-regenerate">Regenerează</button>
                  )}
                  <button type="button" onClick={() => { 
                    setSketchData(null); 
                    setFormData(prev => ({ ...prev, sketchData: null })); 
                  }} className="btn-clear">Șterge</button>
                </div>
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <>
            <div className="form-section full-width">
              <div className="section-header">
                <div className="section-icon gray">📝</div>
                <h2>14. Observații</h2>
              </div>
              <div className="observatii-grid">
                <div className="observatii-column col-a">
                  <div className="observatii-header header-a">VEHICUL A</div>
                  <textarea className="form-textarea" rows="3" placeholder="Observații pentru vehiculul A..."
                    value={formData.observatiiA} onChange={e => updateFormData('observatiiA', e.target.value)} />
                </div>
                <div className="observatii-column col-b">
                  <div className="observatii-header header-b">VEHICUL B</div>
                  <textarea className="form-textarea" rows="3" placeholder="Observații pentru vehiculul B..."
                    value={formData.observatiiB} onChange={e => updateFormData('observatiiB', e.target.value)} />
                </div>
              </div>
            </div>

            <div className="generate-section green">
              <h2>IMPORTANT!</h2>
              <p><strong>Amiabila trebuie semnată de ambii șoferi implicați în accident!</strong></p>
              {sketchData && <p style={{ fontSize: 12, opacity: 0.9, marginBottom: 6 }}>Schița va fi inclusă în PDF</p>}
              
              <div className="contact-simple">
                <p className="contact-intro">Pentru a genera Amiabila, te rugăm să introduci:</p>
                <div className="contact-layout">
                  <div className="contact-fields">
                    <div className="form-group">
                      <label className="form-label">Email (șofer păgubit) <span className="required">*</span></label>
                      <input type="email" className="form-input" placeholder="adresa@email.com"
                        value={emailAddress} onChange={e => setEmailAddress(e.target.value)} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Telefon (șofer păgubit) <span className="required">*</span></label>
                      <input type="tel" className="form-input" placeholder="0722 123 456"
                        value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} required />
                    </div>
                  </div>
                  <div className="contact-info-desktop">
                    <h4>De ce avem nevoie de aceste date</h4>
                    <p>Un consultant Auto Klasen te va contacta pentru a verifica împreună că documentul este corect completat și semnat, evitând astfel respingerea de către asigurator.</p>
                    <p>Ai nevoie de ajutor? <strong>0371 71 91 91</strong></p>
                  </div>
                  <div className="info-dropdown-mobile">
                    <button type="button" className="info-toggle" onClick={() => setIsInfoOpen(!isInfoOpen)}>
                      {isInfoOpen ? '▼' : '▶'} De ce avem nevoie de aceste date?
                    </button>
                    {isInfoOpen && (
                      <div className="info-content">
                        <p>Un consultant Auto Klasen te va contacta pentru a verifica împreună că documentul este corect completat și semnat.</p>
                        <p>Ai nevoie de ajutor? <strong><a href="tel:0371719191">0371 71 91 91</a></strong></p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <button className="btn-generate" onClick={generatePDF} disabled={loading || emailLoading || pdfGenerationCount >= MAX_PDF_GENERATIONS}>
                {loading ? <><span className="spinner"></span> Se generează...</> : 
                 emailLoading ? <><span className="spinner"></span> Se trimite email...</> :
                 pdfGenerationCount >= MAX_PDF_GENERATIONS ? 'Limită atinsă' :
                 'Descarcă Amiabila (PDF)'}
              </button>

              {pdfGenerationCount >= MAX_PDF_GENERATIONS && (
                <div style={{ 
                  background: '#FEF2F2', 
                  border: '2px solid #EF4444', 
                  borderRadius: 12, 
                  padding: '20px 24px', 
                  marginTop: 16, 
                  textAlign: 'center' 
                }}>
                  <p style={{ color: '#991B1B', fontWeight: 700, fontSize: 16, margin: '0 0 8px 0' }}>
                    Se pare că întâmpinați dificultăți în completarea amiabilei.
                  </p>
                  <p style={{ color: '#7F1D1D', fontSize: 14, margin: '0 0 16px 0' }}>
                    Vă rugăm să ne contactați și vă ajutăm noi să o completați corect:
                  </p>
                  <a href="tel:0371719191" style={{ 
                    display: 'inline-block', 
                    background: '#1e3a5f', 
                    color: '#fff', 
                    padding: '14px 32px', 
                    borderRadius: 10, 
                    fontSize: 18, 
                    fontWeight: 700, 
                    textDecoration: 'none' 
                  }}>
                    📞 0371 71 91 91
                  </a>
                </div>
              )}
              
              {pdfGenerated && (
                <SuccessCard emailSent={emailWasSent} emailAddress={emailAddress} />
              )}
            </div>

            <div className="reset-section">
              <p>Ai completat și descărcat formularul? Poți șterge definitiv toate datele introduse.</p>
              <button type="button" className="btn-reset" onClick={resetForm}>Șterge toate datele</button>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className="form-page-layout">
        {/* Coloana principala - formular */}
        <main className="form-main" ref={formContainerRef}>
          <div className="form-toolbar">
            <div className="autosave-indicator">
              <span className="autosave-dot"></span>
              Datele se salvează automat în browser
            </div>
          </div>

          <WizardStepper 
            currentStep={currentStep} 
            onStepClick={goToStep}
            completedSteps={completedSteps}
          />

          {renderStepContent()}

          <div className="wizard-navigation">
            {currentStep > 1 && (
              <button type="button" className="btn-wizard btn-prev" onClick={prevStep}>
                ← Înapoi
              </button>
            )}
            {currentStep < WIZARD_STEPS.length && (
              <button type="button" className="btn-wizard btn-next" onClick={nextStep}>
                Continuă →
              </button>
            )}
          </div>
        </main>

        {/* Sidebar dreapta - doar desktop */}
        <SidebarGuide currentStep={currentStep} />
      </div>

      <div className={`toast ${toast.show ? 'show' : ''} ${toast.error ? 'error' : ''}`}>{toast.message}</div>
    </>
  );
}

export default FormularPage;